import React, { ChangeEvent, ReactNode, useRef, useState } from 'react';
import moment from 'moment';
import { DateField, Chips, InformationPanel } from '@gerdau/hefesto-mais-web';
import { Select } from '@gerdau/hefesto/';
import { DateType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import {
  MeasureUnit,
  UpdateItemProps,
  ValidationModalProps
} from '../../../../services/MVP/models';
import { ValidationModal } from './ValidationModal';
import {
  ChipTitle,
  InformationPanelWrapper,
  QtyButton,
  QtyContainer,
  QtyInput,
  QtyInputButtonsContainer,
  QtyInputContainer,
  QtyLabel
} from './style';
import useDebounce from '../../../../hooks/useDebounce';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { addToast } from '../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../store/ducks/toast/types';
import { isDateBeyondMaxDelivery } from '../../../../utils/formatDate';
import { purchaseOrigin } from '../../../../services/MVP/utils';

interface InputProps {
  item_id: string | number;
  error?: boolean;
  label: string;
  disabled: boolean;
  onChange: (value: UpdateItemProps) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface DateInputProps extends InputProps {
  value?: Date | string;
  endDate?: Date;
  minDate?: Date;
  hasAgreement?: boolean;
}

interface OptionsProps {
  label: string;
  value: string;
}

interface OptionsInputProps extends InputProps {
  options: OptionsProps[];
  value: string;
  type: 'destination' | 'payment' | 'taxBenefit';
  hasAgreement?: boolean;
}

interface QuantityInputProps extends InputProps {
  value: string | number;
  measureUnit: MeasureUnit;
  units: MeasureUnit[];
}

interface ChipInputsProps extends Omit<InputProps, 'onChange'> {
  value: string[];
  onChange: (value: UpdateItemProps) => void;
  measureUnit: MeasureUnit;
}

function InformationPanelContainer({
  refBtn,
  children,
  width,
  title,
  text,
  nearBorder = false
}: {
  refBtn: React.Ref<HTMLButtonElement>;
  children: ReactNode;
  width: string;
  title: string;
  text: string;
  nearBorder?: boolean;
}) {
  return (
    <InformationPanelWrapper>
      <InformationPanel
        position={nearBorder ? 'bottom left' : 'bottom center'}
        panelWidth={width}
        title={title}
        text={text}
        trigger={
          <div>
            <button ref={refBtn}></button>
          </div>
        }
        type="text"
      />
      {children}
    </InformationPanelWrapper>
  );
}

const validationTitle = {
  destination: 'Alterar a destinação afetará o preço acordado',
  payment: 'Alterar a condição de pagamento afetará o preço acordado',
  taxBenefit: 'Alterar o benefício fiscal afetará o preço acordado',
  date: 'Alterar a data desejada afetará o preço acordado'
};

const validationDescription = {
  destination:
    'Será necessário recalcular o preço após essa alteração. Deseja alterar a destinação?',
  payment:
    'Será necessário recalcular o preço após essa alteração. Deseja alterar a condição de pagamento?',
  taxBenefit:
    'Será necessário recalcular o preço após essa alteração. Deseja alterar o benefício fiscal?',
  date: 'Será necessário recalcular o preço após essa alteração. Deseja alterar a data desejada?'
};

export const DateInput: React.FC<DateInputProps> = ({
  error = false,
  label,
  value,
  endDate,
  minDate,
  disabled,
  item_id,
  onChange,
  hasAgreement,
  ...props
}) => {
  const dispatch = useDispatch();
  const tipo_compra = purchaseOrigin();

  const [dateValue, setDateValue] = useState<DateType | string | undefined>(
    value
  );
  const [validationModalInformation, setValidationModalInformation] =
    useState<ValidationModalProps>({
      show: false,
      title: validationTitle['date'],
      description: validationDescription['date'],
      handleNextBtn: () => null,
      handleNextBtnTitle: 'Alterar'
    });

  const handleChange = (inptValue?: DateType | string) => {
    setValidationModalInformation((prev) => ({
      ...prev,
      show: false
    }));

    setDateValue(inptValue);
    return onChange({
      value: inptValue as string,
      type: 'date',
      item_id: item_id as number
    });
  };

  const handleMiddleware = (inptValue: DateType) => {
    if (moment(inptValue).isSame(value) || !inptValue) {
      return null;
    }
    if (isDateBeyondMaxDelivery(inptValue)) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message:
            'A data desejada excede o prazo permitido. Escolha uma data dentro dos próximos 2 anos.',
          durationInMS: 5000
        })
      );
      handleChange(null);
      return;
    }
    const selectedDate = moment(inptValue);
    if (endDate && selectedDate.isAfter(endDate)) {
      setValidationModalInformation((prev) => ({
        ...prev,
        show: true,
        handleNextBtn: () => {
          handleChange(inptValue);
          dispatch(
            sendTagging({
              event: 'fora_do_preco_acordado',
              screenName: 'carrinho',
              tipo_compra: tipo_compra,
              mensagem: validationTitle['date'],
              botao_navegacao: 'Alterar'
            })
          );
        }
      }));
      return;
    }

    return handleChange(inptValue);
  };

  return (
    <>
      <ValidationModal
        {...validationModalInformation}
        handleCloseValidationModal={() => {
          setValidationModalInformation((prev) => ({
            ...prev,
            show: false
          }));
          dispatch(
            sendTagging({
              event: 'fora_do_preco_acordado',
              screenName: 'carrinho',
              tipo_compra: tipo_compra,
              mensagem: validationDescription['date'],
              botao_navegacao: 'Cancelar'
            })
          );
        }}
      />
      <DateField
        helperTextTooltipPlacement={'bottomLeft'}
        helperText="Escolha um dia útil dentro dos próximos 2 anos."
        {...(error && { variant: 'danger' })}
        {...(error && {
          supportMessage: 'Selecione uma data.'
        })}
        {...(minDate && { minDate: moment(minDate, 'YYYY-MM-DD').toDate() })}
        onChange={handleMiddleware}
        label={label}
        name={label}
        selected={
          dateValue ? moment(dateValue, 'YYYY-MM-DD').toDate() : undefined
        }
        {...(endDate &&
          hasAgreement && {
            warningDatesMessage: {
              startDate: moment(endDate, 'YYYY-MM-DD').add(1, 'day').toDate(),
              endDate: moment('2100-12-31T23:59:59.002Z').toDate(),
              infoMessage: 'Fora da validade do preço acordado.'
            }
          })}
        disabled={disabled}
        {...props}
      />
    </>
  );
};

export const OptionsInput: React.FC<OptionsInputProps> = ({
  error = false,
  options,
  label,
  type,
  value,
  disabled,
  item_id,
  onChange,
  hasAgreement,
  ...props
}) => {
  const dispatch = useDispatch();
  const tipo_compra = purchaseOrigin();

  const [optValue, setOptValue] = useState<string | string[]>(value);
  const [validationModalInformation, setValidationModalInformation] = useState<{
    show: boolean;
    title: string;
    description: string;
    value: string | string[];
  }>({
    show: false,
    title: validationTitle[type],
    description: validationDescription[type],
    value: ''
  });

  const handleCloseModal = () => {
    onChange({
      value,
      type,
      item_id: item_id as number
    });

    setValidationModalInformation((prev) => ({
      ...prev,
      value: '',
      show: false
    }));

    dispatch(
      sendTagging({
        event: 'fora_do_preco_acordado',
        screenName: 'carrinho',
        tipo_compra: tipo_compra,
        mensagem: validationDescription[type],
        botao_navegacao: 'Cancelar'
      })
    );
  };

  const handleChange = (inputValue?: string | string[]) => {
    setValidationModalInformation((prev) => ({
      ...prev,
      show: false
    }));
    setOptValue(value);
    onChange({
      value: inputValue ? inputValue : validationModalInformation.value,
      type,
      item_id: item_id as number
    });
    dispatch(
      sendTagging({
        event: 'fora_do_preco_acordado',
        screenName: 'carrinho',
        tipo_compra: tipo_compra,
        mensagem: validationDescription[type],
        botao_navegacao: 'Alterar'
      })
    );
  };

  const handleMiddleware = (inputValue: string | string[]) => {
    if (inputValue === optValue) {
      return null;
    }

    if (hasAgreement && inputValue !== value) {
      setValidationModalInformation((prev) => ({
        ...prev,
        ...(inputValue && { value: inputValue }),
        show: true
      }));
      return;
    }
    return handleChange(inputValue);
  };

  return (
    <>
      <ValidationModal
        {...validationModalInformation}
        handleNextBtn={handleChange}
        handleCloseValidationModal={handleCloseModal}
      />
      <Select
        {...(error && { variant: 'danger' })}
        {...(error && {
          supportMessage:
            type === 'payment' ? 'Condição inválida.' : 'Selecione uma opção.'
        })}
        onSelect={handleMiddleware}
        value={optValue}
        options={options}
        label={label}
        name={label}
        disabled={disabled}
        {...props}
      />
    </>
  );
};

export const QuantityInput: React.FC<QuantityInputProps> = ({
  value,
  disabled,
  item_id,
  onChange,
  measureUnit,
  information,
  changeInformation
}) => {
  const dispatch = useDispatch();
  const tipo_compra = purchaseOrigin();
  const multiple = Number(measureUnit.multiple);
  const measureUnitminQty = Number(measureUnit.minimumQuantity);
  const hasMinimumQty = measureUnitminQty !== 1;
  const minQty = hasMinimumQty
    ? Math.ceil(measureUnitminQty / multiple)
    : measureUnitminQty;
  const minimunOrderQty = hasMinimumQty
    ? Math.ceil(measureUnitminQty / multiple) * multiple
    : measureUnitminQty * multiple;

  const informationPanelButton = useRef<HTMLButtonElement>(null);

  const convertToShow = (valueToShow: string | number) => {
    const result =
      Number(valueToShow) * multiple <= minimunOrderQty
        ? minimunOrderQty
        : Number(valueToShow) * multiple;

    return result % 1 === 0 ? result.toFixed(0) : result.toFixed(2);
  };

  const [qtyToShow, setQtyToShow] = useState(convertToShow(value));

  const handleChangeText = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    const regex = /^\d+\.?\d{0,2}$/;

    if (inputValue === '' || regex.test(inputValue)) {
      setQtyToShow(inputValue);

      debouncedChange(inputValue);
    }
  };

  const handleChangeTextDebounce = (e: string) => {
    if (Number(e) < minQty) {
      setQtyToShow(convertToShow(minQty));
      onChange({
        value: minQty,
        type: 'quantity',
        item_id: item_id as number
      });

      if (
        informationPanelButton.current &&
        information.canShow.time - new Date().getTime() <= 0 &&
        !information.canShow.minQty
      ) {
        dispatch(
          sendTagging({
            event: 'qtde_minima_itens',
            screenName: 'carrinho',
            tipo_compra: tipo_compra
          })
        );
        changeInformation({
          title: 'Quantidade mínima de itens',
          text: 'Não é possível reduzir a quantidade de itens abaixo do mínimo para envio.',
          canShow: {
            time: new Date().getTime() + 2000,
            minQty: true,
            personalize: false
          }
        });
        informationPanelButton.current.click();
      }

      return;
    }

    changeInformation({
      ...information,
      canShow: {
        time: information.canShow.time,
        minQty: false,
        personalize: false
      }
    });

    if (!(Number(e) % Number(multiple))) {
      setQtyToShow(convertToShow(Number(e) / Number(multiple)));
      onChange({
        value: Number(e) / Number(multiple),
        type: 'quantity',
        item_id: item_id as number
      });
      return;
    }

    if (
      informationPanelButton.current &&
      information.canShow.time - new Date().getTime() <= 0 &&
      !information.canShow.personalize
    ) {
      changeInformation({
        title: 'Quantidade personalizada',
        text: 'O sistema ajusta a quantidade para o múltiplo mais próximo da unidade de medida do produto quando você insere um valor.',
        canShow: {
          time: new Date().getTime() + 2000,
          personalize: true,
          minQty: false
        }
      });
      informationPanelButton.current.click();
    }
    const convertMultiple = Math.ceil(Number(e) / Number(multiple));
    setQtyToShow(convertToShow(convertMultiple));
    onChange({
      value: convertMultiple,
      type: 'quantity',
      item_id: item_id as number
    });
    return;
  };

  const debouncedChange = useDebounce(handleChangeTextDebounce, 1000);

  const increaseChange = () => {
    changeInformation({
      ...information,
      canShow: {
        time: information.canShow.time,
        minQty: false,
        personalize: false
      }
    });
    if (Number(value) + 1 <= minQty) {
      onChange({
        value: minQty + 1,
        type: 'quantity',
        item_id: item_id as number
      });
      return;
    }

    setQtyToShow(convertToShow(Number(value) + 1));
    onChange({
      value: Number(value) + 1,
      type: 'quantity',
      item_id: item_id as number
    });
  };

  const decreaseChange = () => {
    if (Number(value) - 1 < minQty) {
      onChange({
        value: minQty,
        type: 'quantity',
        item_id: item_id as number
      });

      if (
        informationPanelButton.current &&
        information.canShow.time - new Date().getTime() <= 0 &&
        !information.canShow.minQty
      ) {
        dispatch(
          sendTagging({
            event: 'qtde_minima_itens',
            screenName: 'carrinho',
            tipo_compra: tipo_compra
          })
        );
        changeInformation({
          title: 'Quantidade mínima de itens',
          text: 'Não é possível reduzir a quantidade de itens abaixo do mínimo para envio.',
          canShow: {
            time: new Date().getTime() + 2000,
            personalize: false,
            minQty: true
          }
        });
        informationPanelButton.current.click();
      }
      return;
    }

    changeInformation({
      ...information,
      canShow: {
        time: information.canShow.time,
        minQty: false,
        personalize: false
      }
    });

    setQtyToShow(convertToShow(Number(value) - 1));
    onChange({
      value: Number(value) - 1,
      type: 'quantity',
      item_id: item_id as number
    });
  };

  return (
    <InformationPanelContainer
      title={information.title}
      text={information.text}
      width="368px"
      refBtn={informationPanelButton}
    >
      <QtyContainer disabled={disabled}>
        <QtyLabel>Quantidade</QtyLabel>
        <QtyInputContainer>
          <QtyInput
            type="text"
            autoComplete="off"
            onChange={handleChangeText}
            value={qtyToShow}
            disabled={disabled}
          />
          <QtyInputButtonsContainer>
            <QtyButton onClick={increaseChange} disabled={disabled}>
              <svg
                viewBox="0 0 24 24"
                className="caret-up"
                width="16px"
                height="16px"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2.12229 19.4792C1.947 19.1581 1.96101 18.767 2.15882 18.4592L11.1588 4.45924C11.3428 4.17302 11.6597 4 12 4C12.3403 4 12.6572 4.17302 12.8412 4.45924L21.8412 18.4592C22.039 18.767 22.053 19.1581 21.8777 19.4792C21.7024 19.8003 21.3658 20 21 20L3 20C2.63419 20 2.29758 19.8003 2.12229 19.4792Z"
                  fill="currentColor"
                ></path>
              </svg>
            </QtyButton>
            <QtyButton
              onClick={decreaseChange}
              disabled={disabled || information.canShow.minQty}
            >
              <svg
                viewBox="0 0 24 24"
                className="caret-down"
                width="16px"
                height="16px"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M21.8777 4.52081C22.053 4.84189 22.039 5.23305 21.8412 5.54076L12.8412 19.5408C12.6572 19.827 12.3403 20 12 20C11.6597 20 11.3428 19.827 11.1588 19.5408L2.15882 5.54076C1.96101 5.23305 1.947 4.84188 2.12229 4.52081C2.29758 4.19974 2.63419 4 3 4L21 4C21.3658 4 21.7024 4.19974 21.8777 4.52081Z"
                  fill="currentColor"
                ></path>
              </svg>
            </QtyButton>
          </QtyInputButtonsContainer>
        </QtyInputContainer>
      </QtyContainer>
    </InformationPanelContainer>
  );
};

export const ChipInputs: React.FC<ChipInputsProps> = ({
  error = false,
  options,
  label,
  value,
  disabled,
  onChange,
  item_id,
  information,
  changeInformation,
  ...props
}) => {
  const [chipValue, setChipValue] = useState(value);
  const informationPanelButtonChip = useRef<HTMLButtonElement>(null);

  const handleChange = (chip: string[]) => {
    if (chip.length && chip[0] !== chipValue[0]) {
      if (
        informationPanelButtonChip.current &&
        information.canShow.time - new Date().getTime() <= 0
      ) {
        changeInformation({
          title: 'Alteração de unidade de medida',
          text: 'O sistema ajusta proporcionalmente a quantidade quando você altera a unidade de medida.',
          canShow: {
            ...information.canShow,
            time: new Date().getTime() + 2000
          }
        });
        informationPanelButtonChip.current.click();
      }
      setChipValue(chip);
      onChange({ value: chip[0], type: 'chip', item_id });
    }
  };

  return (
    <InformationPanelContainer
      title={information.title}
      text={information.text}
      width="368px"
      refBtn={informationPanelButtonChip}
      nearBorder={true}
    >
      <ChipTitle>{label}</ChipTitle>
      <Chips
        {...(error && { variant: 'danger' })}
        label={label}
        chips={options}
        handleSelect={handleChange}
        type="choice"
        disabled={disabled}
        selectedChipIds={chipValue}
        {...props}
      />
    </InformationPanelContainer>
  );
};
